<script setup>
import { computed, ref } from 'vue';
import { route } from 'ziggy-js';
import { usePage, Link } from '@inertiajs/vue3';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import Select from 'primevue/select';
import Message from 'primevue/message';
import SplitButton from 'primevue/splitbutton';
import InputText from 'primevue/inputtext';
import SelectButton from 'primevue/selectbutton';
import { useI18n } from 'vue-i18n';
import axios from 'axios';

defineOptions({
  inheritAttrs: false
});

const props = defineProps({
  showButton: {
    type: Boolean,
    default: true
  }
});

const emit = defineEmits(['sessionCreated']);

const { t } = useI18n();
const page = usePage();
const team = page.props.team;

const showCreateSessionButton = computed(() => {
  return props.showButton && team !== null;
});

// Dialog state
const createDialogVisible = ref(false);
const sessionLink = ref('');
const sessionLinkExpires = ref('');
const sessionLinkTeamSessionReference = ref('');
const errorMessage = ref('');
const createLoading = ref(false);
const products = ref([]);
const productsLoaded = ref(false);
const selectedProduct = ref(null);
const backgroundMode = ref('background');
const directRenderEnabled = ref(false);
const directRenderImageCount = ref(4);

const backgroundModeOptions = computed(() => [
  {
    label: t('admin.sessions.create.background_mode_background'),
    value: 'background'
  },
  {
    label: t('admin.sessions.create.background_mode_transparent'),
    value: 'transparent'
  }
]);

const formattedExpiresMessage = computed(() => {
  return sessionLinkExpires.value ? new Date(sessionLinkExpires.value).toLocaleString() : '';
});

const canCreateSession = computed(() => {
  return selectedProduct.value !== null;
});

const directRenderImageCountOptions = computed(() => ([
  { label: '1', value: 1 },
  { label: '2', value: 2 },
  { label: '3', value: 3 },
  { label: '4', value: 4 }
]));

const resetForm = () => {
  sessionLink.value = '';
  sessionLinkExpires.value = '';
  sessionLinkTeamSessionReference.value = '';
  errorMessage.value = '';
  selectedProduct.value = null;
  backgroundMode.value = 'background';
  directRenderEnabled.value = false;
  directRenderImageCount.value = 4;
};

const openCreateDialog = () => {
  resetForm();
  productsLoaded.value = false;
  createDialogVisible.value = true;

  // Load products
  if (team?.id) {
    axios.get(route('admin.api.teams.products', team.id))
      .then(response => {
        products.value = response.data.products;
        productsLoaded.value = true;
      })
      .catch(error => {
        console.error(error);
        errorMessage.value = t('admin.sessions.create.error_loading_products');
      });
  } else {
    errorMessage.value = t('admin.sessions.create.error_no_team');
  }
};

const getApiUserToken = async (asAdmin) => {
  return new Promise((resolve, reject) => {
    if (!team?.id) {
      reject(new Error(t('admin.sessions.create.error_no_team')));
      return;
    }
    axios.post(route('admin.api.teams.create-session', team.id), {
      asAdmin: asAdmin
    }, {
      withCredentials: true
    })
      .then(response => {
        const bearerToken = response.data.token;
        resolve(bearerToken);
      })
      .catch(error => {
        console.error(error);
        reject(error);
      });
  });
};

const createSession = (asAdmin = true) => {
  errorMessage.value = '';
  createLoading.value = true;

  getApiUserToken(asAdmin).then(token => {
    axios.post(
      route('public-api.session.createSignedUrl', {
        sku: selectedProduct.value.sku,
      }),
      {
        team: team?.id,
        transparent_background: backgroundMode.value === 'transparent',
        direct_render_enabled: directRenderEnabled.value,
        direct_render_image_count: directRenderImageCount.value,
      },
      {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    )
      .then(response => {
        sessionLink.value = response.data.url;
        sessionLinkExpires.value = response.data.expires;
        sessionLinkTeamSessionReference.value = response.data.teamSessionReference;

        // Emit event to parent component to refresh sessions list
        emit('sessionCreated', {
          url: response.data.url,
          expires: response.data.expires,
          reference: response.data.teamSessionReference
        });
      })
      .catch(error => {
        console.error(error);
        errorMessage.value = error.response?.data?.message || t('admin.sessions.create.error_generic');
      })
      .finally(() => {
        createLoading.value = false;
      });
  }).catch(error => {
    createLoading.value = false;
    errorMessage.value = error.response?.data?.message || t('admin.sessions.create.error_generic');
  });
};

const openInNewTab = () => {
  window.open(sessionLink.value, '_blank');
};

const copySuccess = ref(false);

const copySessionLink = () => {
  if (sessionLink.value && navigator.clipboard) {
    navigator.clipboard.writeText(sessionLink.value).then(() => {
      copySuccess.value = true;
      setTimeout(() => { copySuccess.value = false; }, 2000);
    });
  }
};

const failedPreviewIds = ref(new Set());

const hasProductPreview = (product) => {
  return Boolean(product?.preview_url)
    && product.preview_status !== 'missing'
    && !failedPreviewIds.value.has(product.id);
};

const onProductPreviewError = (product) => {
  failedPreviewIds.value = new Set([...failedPreviewIds.value, product.id]);
};

const createSessionItems = computed(() => [
  {
    label: t('admin.sessions.create.create_user_session'),
    icon: 'pi pi-fw pi-plus',
    command: () => createSession(false)
  },
  {
    label: t('admin.sessions.create.create_admin_session'),
    icon: 'pi pi-fw pi-plus',
    command: () => createSession(true)
  }
]);

// Expose openCreateDialog for external use
defineExpose({
  openCreateDialog
});
</script>

<template>
  <!-- Trigger Button -->
  <Button
    v-if="showCreateSessionButton"
    v-bind="$attrs"
    :label="t('admin.sessions.create.button')"
    icon="pi pi-plus"
    @click="openCreateDialog"
    severity="primary"
    class="white-space-nowrap flex-shrink-0"
  />

  <!-- Dialog -->
  <Dialog
    v-model:visible="createDialogVisible"
    modal
    :header="t('admin.sessions.create.title', { team: (team?.name || '...') })"
    :style="{ width: '50rem' }"
  >
    <div class="flex flex-column gap-3">
      <Message v-if="errorMessage" severity="error">{{ errorMessage }}</Message>

      <!-- Form: only visible when no session created yet -->
      <template v-if="!sessionLink">
        <div v-if="products.length > 0" class="flex flex-column gap-2">
          <label for="product">{{ t('admin.sessions.create.select_product') }}</label>
          <Select
            id="product"
            v-model="selectedProduct"
            :options="products"
            optionLabel="name"
            :filter="true"
            :filterFields="['name', 'sku']"
            :filterPlaceholder="t('admin.product.search_placeholder')"
            :placeholder="t('admin.sessions.create.choose_product')"
            class="w-full create-session-product-select"
          >
            <template #option="slotProps">
              <div class="flex align-items-center gap-2 w-full">
                <img
                  v-if="hasProductPreview(slotProps.option)"
                  :src="slotProps.option.preview_url"
                  :alt="slotProps.option.name"
                  class="create-session-product-select__preview border-round"
                  @error="onProductPreviewError(slotProps.option)"
                />
                <div
                  v-else
                  class="create-session-product-select__preview create-session-product-select__preview--placeholder border-round surface-200 flex align-items-center justify-content-center"
                >
                  <i class="pi pi-image text-400"></i>
                </div>
                <div class="flex flex-column min-w-0">
                  <span class="white-space-nowrap overflow-hidden text-overflow-ellipsis">{{ slotProps.option.name }}</span>
                  <span class="text-color-secondary text-sm">{{ slotProps.option.sku }}</span>
                </div>
              </div>
            </template>
            <template #value="slotProps">
              <div v-if="slotProps.value" class="flex align-items-center gap-2">
                <img
                  v-if="hasProductPreview(slotProps.value)"
                  :src="slotProps.value.preview_url"
                  :alt="slotProps.value.name"
                  class="create-session-product-select__preview create-session-product-select__preview--selected border-round"
                  @error="onProductPreviewError(slotProps.value)"
                />
                <div
                  v-else
                  class="create-session-product-select__preview create-session-product-select__preview--selected create-session-product-select__preview--placeholder border-round surface-200 flex align-items-center justify-content-center"
                >
                  <i class="pi pi-image text-400 text-xs"></i>
                </div>
                <span>{{ slotProps.value.name }}</span>
              </div>
              <span v-else>{{ t('admin.sessions.create.choose_product') }}</span>
            </template>
          </Select>
        </div>

        <div v-if="products.length > 0" class="flex flex-column gap-2">
          <label for="background-mode">{{ t('admin.sessions.create.background_mode_label') }}</label>
          <SelectButton
            id="background-mode"
            v-model="backgroundMode"
            :options="backgroundModeOptions"
            optionLabel="label"
            optionValue="value"
            :allowEmpty="false"
            aria-labelledby="background-mode"
          />
          <small class="admin-help-text">{{ t('admin.sessions.create.background_mode_help') }}</small>
        </div>

        <div v-if="products.length > 0" class="flex flex-column gap-2">
          <label for="direct-render-enabled">{{ t('admin.sessions.create.render_mode_label') }}</label>
          <SelectButton
            id="direct-render-enabled"
            v-model="directRenderEnabled"
            :options="[
              { label: t('admin.sessions.create.render_mode_offline'), value: false },
              { label: t('admin.sessions.create.render_mode_direct'), value: true }
            ]"
            optionLabel="label"
            optionValue="value"
            :allowEmpty="false"
          />
          <small class="admin-help-text">{{ t('admin.sessions.create.render_mode_help') }}</small>
        </div>

        <div v-if="products.length > 0 && directRenderEnabled" class="flex flex-column gap-2">
          <label for="direct-render-image-count">{{ t('admin.sessions.create.render_image_count_label') }}</label>
          <SelectButton
            id="direct-render-image-count"
            v-model="directRenderImageCount"
            :options="directRenderImageCountOptions"
            optionLabel="label"
            optionValue="value"
            :allowEmpty="false"
          />
          <small class="admin-help-text">{{ t('admin.sessions.create.render_image_count_help') }}</small>
        </div>

        <div v-if="productsLoaded && products.length === 0" class="flex flex-column align-items-center gap-3 py-4">
          <i class="pi pi-info-circle text-primary-400" style="font-size: 2rem"></i>
          <div class="text-center">
            <p class="mb-2">{{ t('admin.sessions.create.no_products') }}</p>
            <Link :href="route('admin.products.index')" class="text-primary font-semibold underline" @click="createDialogVisible = false">
              {{ t('admin.sessions.create.create_product_link') }}
            </Link>
          </div>
        </div>
      </template>

      <!-- Session created: result view -->
      <div v-if="sessionLink" class="flex flex-column gap-4">
        <!-- Success header -->
        <div class="flex align-items-center gap-3 p-3 border-round surface-100">
          <i class="pi pi-check-circle" style="font-size: 2rem; color: var(--green-500)"></i>
          <div>
            <div class="admin-section-title">{{ t('admin.sessions.create.session_created') }}</div>
            <div class="admin-section-desc">{{ selectedProduct?.name }}</div>
          </div>
        </div>

        <!-- Link -->
        <div class="flex flex-column gap-2">
          <label class="font-semibold">{{ t('admin.sessions.create.session_link') }}</label>
          <InputText v-model="sessionLink" readonly class="w-full" />
          <div class="flex gap-2 mt-1">
            <Button
              :label="copySuccess ? t('admin.sessions.create.copied') : t('admin.common.copy')"
              :icon="copySuccess ? 'pi pi-check' : 'pi pi-copy'"
              :severity="copySuccess ? 'success' : 'secondary'"
              class="flex-1"
              @click="copySessionLink"
            />
            <Button
              :label="t('admin.sessions.create.open')"
              icon="pi pi-external-link"
              severity="primary"
              class="flex-1"
              @click="openInNewTab"
            />
          </div>
        </div>

        <!-- Meta info -->
        <div class="flex flex-column gap-2 p-3 border-round surface-50 border-1 border-200">
          <div class="flex justify-content-between align-items-center">
            <span class="admin-meta-label">{{ t('admin.sessions.create.expires') }}</span>
            <span class="admin-meta-value">{{ formattedExpiresMessage }}</span>
          </div>
          <div class="flex justify-content-between align-items-center">
            <span class="admin-meta-label">{{ t('admin.sessions.create.reference') }}</span>
            <span class="admin-meta-value font-monospace">{{ sessionLinkTeamSessionReference }}</span>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-content-between align-items-center gap-2">
        <Button
          v-if="sessionLink"
          :label="t('admin.sessions.create.create_another')"
          icon="pi pi-refresh"
          severity="secondary"
          outlined
          @click="resetForm"
        />
        <div class="flex gap-2 ml-auto">
          <Button
            :label="t('admin.sessions.create.close')"
            severity="secondary"
            @click="createDialogVisible = false"
          />
          <SplitButton
            v-if="!sessionLink"
            :label="t('admin.sessions.create.button')"
            :loading="createLoading"
            :disabled="!canCreateSession"
            @click="createSession(false)"
            :model="createSessionItems"
            class="white-space-nowrap"
          />
        </div>
      </div>
    </template>
  </Dialog>
</template>

<style scoped>
.font-monospace {
  font-family: monospace;
}

.create-session-product-select__preview {
  width: 40px;
  height: 30px;
  object-fit: cover;
  flex-shrink: 0;
}

.create-session-product-select__preview--selected {
  width: 30px;
  height: 22px;
}

.create-session-product-select__preview--placeholder {
  font-size: 0.875rem;
}
</style>
